import { useCallback, useRef, type KeyboardEvent, type ClipboardEvent } from "react";

type Props = {
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  autoFocus?: boolean;
};

const LENGTH = 6;

/**
 * Six single-digit inputs with auto-advance, backspace, and paste support.
 */
export default function OtpInput({ value, onChange, disabled, autoFocus }: Props) {
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);

  const digits = Array.from({ length: LENGTH }, (_, i) => value[i] ?? "");

  const focusAt = useCallback((i: number) => {
    const el = inputsRef.current[Math.max(0, Math.min(LENGTH - 1, i))];
    el?.focus();
    el?.select();
  }, []);

  const setFromFullString = useCallback(
    (raw: string) => {
      const only = raw.replace(/\D/g, "").slice(0, LENGTH);
      onChange(only);
      focusAt(only.length >= LENGTH ? LENGTH - 1 : only.length);
    },
    [onChange, focusAt]
  );

  const handleChange = (index: number, char: string) => {
    if (disabled) return;
    const d = char.replace(/\D/g, "").slice(-1) ?? "";
    const next = (value.slice(0, index) + d + value.slice(index + 1)).slice(0, LENGTH);
    onChange(next);
    if (d) focusAt(index + 1);
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      if (digits[index]) {
        onChange(value.slice(0, index) + value.slice(index + 1));
      } else if (index > 0) {
        focusAt(index - 1);
        onChange(value.slice(0, index - 1) + value.slice(index));
      }
      e.preventDefault();
    }
    if (e.key === "ArrowLeft" && index > 0) {
      focusAt(index - 1);
      e.preventDefault();
    }
    if (e.key === "ArrowRight" && index < LENGTH - 1) {
      focusAt(index + 1);
      e.preventDefault();
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text") || "";
    setFromFullString(text);
  };

  return (
    <div className="w-100" role="group" aria-label="Verification code">
      <div className="auth-otp-row d-flex flex-row justify-content-center align-items-center flex-nowrap col-10 col-md-12 mx-auto px-0 gap-1 gap-md-2">
        {digits.map((digit, i) => (
          <div
            key={i}
            className="auth-otp-cell-box d-flex align-items-center justify-content-center flex-fill min-w-0 bg-white border rounded-2"
          >
            <input
              ref={(el) => {
                inputsRef.current[i] = el;
              }}
              type="text"
              inputMode="numeric"
              autoComplete={i === 0 ? "one-time-code" : "off"}
              maxLength={1}
              className="auth-otp-cell w-100 h-100 m-0 p-0 border-0 bg-transparent text-dark fw-semibold text-center lh-1 fs-6"
              value={digit}
              disabled={disabled}
              autoFocus={autoFocus && i === 0}
              id={i === 0 ? "otp-digit-0" : undefined}
              aria-label={`Digit ${i + 1} of ${LENGTH}`}
              onChange={(e) => handleChange(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              onPaste={handlePaste}
              onFocus={(e) => e.target.select()}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
